import { MONTHS, WEEKDAYS_FROM_MON } from '../common/calendar';
import { isBlank, isEmpty, isNotPresent, mergeDeep, MergeOptions, overWriteArray } from '../utils';
import { Localizations, localizations } from './localizations';
import { Plural } from './types';

const parseEnumArray = <O>(enumArray: ReadonlyArray<unknown>, cms: unknown[]): O => {
  if (isEmpty(cms)) {
    return {} as O;
  }
  return enumArray.reduce((acc: O, item: any, i: number) => {
    acc[item] = cms[i];
    return acc;
  }, {}) as O;
};

const parsePlural = (one: string, many: string, none?: string): Plural => {
  if (isNotPresent(one) && isNotPresent(many) && isNotPresent(none)) {
    return undefined;
  }
  const fallback: string = one || many || none;
  return { none: none || fallback, one: one || fallback, many: many || fallback };
};

const fixPlaceholders = (translation: string, ...placeholders: string[]): string => {
  if (isBlank(translation)) {
    return translation;
  }
  if (isEmpty(placeholders)) {
    // "some {item} count {mark}" => "some {{item}} count {{mark}}"
    return translation.replace(/([{}])/g, '$1$1');
  }
  // placeholders ['item', 'mark']: "some {0} count {1}" => "some {{item}} count {{mark}}"
  return placeholders.reduce(
    (acc: string, placeholder: string, i: number) => acc.replace(new RegExp(`\\{${i}\\}`, 'g'), placeholder),
    translation
  );
};

const ariaActiveLocation = (countryTrans: string, typeTrans: string, locationCodeTrans: string): string => {
  const country: string = countryTrans ? fixPlaceholders(countryTrans, '{{country}}') : 'Country {{country}}';
  const type: string = typeTrans ? `${typeTrans} {{title}}` : '{{type}} {{title}}';
  const locationCode: string = locationCodeTrans
    ? fixPlaceholders(locationCodeTrans, '{{locationCode}}')
    : 'location code {{locationCode}}';

  return `${type}, ${locationCode}, ${country}.`;
};

const normalizeCurlyBraces = (localizations: Localizations): Localizations => {
  const normalized = { ...localizations } as Localizations;
  const entries = Object.entries(normalized);

  entries.forEach(([key, value]) => {
    if (typeof value === 'object') {
      normalized[key] = normalizeCurlyBraces(value);
    } else if (typeof value === 'string') {
      // replace single curly braces with double curly braces
      normalized[key] = value.replace(/{{(.*?)}}(?!})/gi, '{$1}').replace(/{(.*?)}(?!})/gi, '{$&}');
    } else {
      normalized[key] = value;
    }
  });

  return normalized;
};

// These needs to be added to CMS
const TEMP_LOCALIZATIONS: Record<string, unknown> = {
  aurinkomatkat: {
    title: 'Varaa lento + hotelli',
    inCooperation: 'Yhteistyössä <b>Aurinkomatkojen</b> kanssa',
    continueModal: {
      title: 'Aurinkomatkojen {holidayType}',
      contentTitle: 'Varauksesi jatkuu Aurinkomatkojen sivuilla',
      content:
        'Lento + hotelli -matkat tarjotaan yhteistyössä Aurinkomatkojen kanssa. Palvelu on saatavissa vain suomeksi.',
    },
    calendar: {
      recommendedTravelDates: 'Suositellut matkustuspäivät',
    },
    duration: 'Kesto',
    selectDuration: 'Valitse kesto',
    paxAmount: {
      selectAge: 'Valitse ikä',
      addRoom: 'Lisää huone',
      removeRoom: 'Poista huone {roomNumber}',
      room: 'Huone',
      year: 'vuosi',
      years: 'vuotta',
    },
    notification: {
      teaser: 'Kolminkertaiset Finnair Plus -pisteet Lento + hotelli -matkoista',
      content:
        'Varaa lento ja hotelli yhdessä ja nauti matkapakettilain tuomasta turvasta. Nyt saat matkastasi myös kolminkertaiset Finnair Plus -pisteet.',
    },
    priceText: 'per aikuinen',
  },
};

const MERGE_OPTIONS: MergeOptions = { overwriteWithEmpty: false, arrayMerge: overWriteArray };
/**
 * Maps given localizations object into booking-widget's internal format
 * @param cms Localizations object from the cms
 * @returns Mapped localizations object for the booking-widget to consume.
 */
export const parseLocalizations = (cms: Record<string, any>): Localizations => {
  cms = mergeDeep([TEMP_LOCALIZATIONS, cms], MERGE_OPTIONS);

  const a11y: any = cms?.bookingSearch?.a11y;

  const parsed: Localizations = {
    continueButton: {
      search: cms?.bookingSearch?.search,
      ariaLabel: cms?.bookingSearch?.bookNowAriaLabel,
    },
    startingPrice: {
      title: cms?.priceCalendar?.perAdult,
      pricesFrom: cms?.priceCalendar?.pricesFrom,
    },
    date: {
      formats: {
        fullDetailed: cms?.date?.dateFormatFullDetailed,
        full: cms?.date?.dateFormatFull,
        date: cms?.date?.dateFormatLong,
        monthAndYear: cms?.date?.dateFormatMonthOfYear,
        dateAndMonthFull: cms?.date?.dateFormatFullNoYear,
        dateAndMonth: cms?.date?.dateFormatShort,
      },
      weekdaysShort: parseEnumArray(WEEKDAYS_FROM_MON, cms?.date?.weekdaysShort),
      weekdays: parseEnumArray(WEEKDAYS_FROM_MON, cms?.date?.weekdays),
      monthsShort: parseEnumArray(MONTHS, cms?.date?.monthNamesShort),
      months: parseEnumArray(MONTHS, cms?.date?.monthNames),
    },

    dateSelector: {
      departure: {
        title: cms?.bookingSearch?.boundSelection?.departureDate,
        placeholder: cms?.priceCalendar?.selectDeparture,
      },
      return: {
        title: cms?.bookingSearch?.boundSelection?.returnDate,
        placeholder: cms?.priceCalendar?.selectReturn,
      },
    },
    dateModal: {
      dateFormatDescription: cms?.priceCalendar?.dateFormatDescription,
      inputPlaceholder: cms?.priceCalendar?.inputPlaceholder,
      invalidDate: cms?.priceCalendar?.invalidDate,
      reset: cms?.actions?.reset,
      resetTravelDates: cms?.priceCalendar?.resetTravelDates,
      selectDeparture: cms?.priceCalendar?.selectDeparture,
      selectReturn: cms?.priceCalendar?.selectReturn,
      title: cms?.priceCalendar?.title,
      flexibleDates: cms?.priceCalendar?.flexibleDates,
      directFlights: cms?.priceCalendar?.directFlights,
      directFlightsOnly: cms?.timetable?.filter?.directFlights?.label,
    },
    locationSelector: {
      allAirportsLabel: cms?.bookingSearch.allAirportsLabel,
      origin: {
        title: cms?.bookingSearch?.from?.title,
        placeholder: cms?.bookingSearch?.from?.placeholder,
      },
      destination: {
        title: cms?.bookingSearch?.to?.title,
        placeholder: cms?.bookingSearch?.to?.placeholder,
      },
    },
    locationSwitcher: {
      ariaLabel: cms?.bookingSearch?.aria?.fromToSwitch,
    },
    locationModal: {
      origin: {
        inputPlaceholder: cms?.bookingSearch?.from?.inputPlaceholder,
        title: cms?.bookingSearch?.from?.title,
        listTitle: cms?.bookingSearch?.from?.header,
        nearestAiport: cms?.bookingSearch?.from?.nearestAiport,
        results: parsePlural(
          fixPlaceholders(a11y?.found?.locations, '{{count}}'),
          fixPlaceholders(a11y?.found?.locations, '{{count}}'),
          fixPlaceholders(a11y?.found?.notFound, '{{query}}')
        ),
      },
      destination: {
        inputPlaceholder: cms?.bookingSearch?.to?.inputPlaceholder,
        title: cms?.bookingSearch?.to?.title,
        listTitle: cms?.bookingSearch?.to?.headerNoFrom,
        results: parsePlural(
          fixPlaceholders(a11y?.found?.destinations, '{{count}}'),
          fixPlaceholders(a11y?.found?.destinations, '{{count}}'),
          fixPlaceholders(a11y?.found?.notFound, '{{query}}')
        ),
      },
      locateMe: cms?.bookingSearch?.locateMe,
      trending: cms?.trendingDestinations,
      previousSearches: cms?.bookingSearch?.previousSearches,
      aria: {
        airport: ariaActiveLocation(
          a11y?.description?.country,
          a11y?.description?.type?.airport,
          a11y?.description?.locationCode
        ),
        city: ariaActiveLocation(
          a11y?.description?.country,
          a11y?.description?.type?.city,
          a11y?.description?.locationCode
        ),
        state: ariaActiveLocation(
          a11y?.description?.country,
          a11y?.description?.type?.state,
          a11y?.description?.locationCode
        ),
      },
      selectionKeys: a11y?.tooltips?.selectionKeys,
    },
    paxAmount: {
      adults: parsePlural(cms?.passenger?.adult, cms?.passenger?.adults),
      children: parsePlural(cms?.passenger?.child, cms?.passenger?.children),
      c15s: parsePlural(cms?.passenger?.child, cms?.passenger?.children),
      infants: parsePlural(cms?.passenger?.infant, cms?.passenger?.infants),
    },
    paxAmountSelector: {
      title: cms?.passengers,
    },
    paxAmountModal: {
      title: cms?.passengers,
      age: {
        adults: cms?.passenger?.widgetAge?.adult,
        children: cms?.passenger?.widgetAge?.child,
        c15s: cms?.passenger?.widgetAge?.c15,
        infants: cms?.passenger?.widgetAge?.infant,
      },
      ageRule: {
        adults: cms?.passenger?.widgetAgeRule?.adult,
        children: cms?.passenger?.widgetAgeRule?.child,
        c15s: cms?.passenger?.widgetAgeRule?.child,
        infants: cms?.passenger?.widgetAgeRule?.infant,
      },
      add: {
        adults: cms?.passengerLabels?.addAdult,
        children: cms?.passengerLabels?.addChild,
        c15s: cms?.passengerLabels?.addC15,
        infants: cms?.passengerLabels?.addInfant,
      },
      remove: {
        adults: cms?.passengerLabels?.removeAdult,
        children: cms?.passengerLabels?.removeChild,
        c15s: cms?.passengerLabels?.removeC15,
        infants: cms?.passengerLabels?.removeInfant,
      },
    },
    paxAmountDetails: {
      sum: {
        one: fixPlaceholders(cms?.passenger?.singlePassenger),
        many: fixPlaceholders(cms?.passenger?.multiplePassengers),
      },
    },
    paxAndClassSelector: {
      label: cms?.passengersAndClass.label,
    },
    travelClass: {
      mixed: cms?.travelClass?.mixed,
      mixedCombined: cms?.travelClass?.mixedCombined,
      economy: cms?.travelClass?.economy,
      ecopremium: cms?.travelClass?.ecopremium,
      business: cms?.travelClass?.business,
    },
    travelClassSelector: {
      title: cms?.travelClasses,
    },
    travelClassModal: {
      title: cms?.travelClasses,
    },
    travelType: {
      oneway: cms?.travelType?.oneWay,
      return: cms?.travelType?.return,
      awardFlight: cms?.travelType?.awardFlight,
      multiCity: cms?.travelType?.multiCity,
      aroundTheWorld: cms?.travelType?.aroundTheWorld,
      aurinkomatkat: cms?.aurinkomatkat?.brandLabel,
      label: cms?.travelType.label,
    },
    number: {
      thousandSeparator: cms?.number?.thousandSeparator,
      decimalSeparator: cms?.number?.decimalSeparator,
      currencyFormat: cms?.number?.currencyFormat,
      currency: cms?.number?.currency,
      thousandAbbreviation: cms?.number?.thousandAbbreviation,
      millionAbbreviation: cms?.number?.millionAbbreviation,
    },
    notification: {
      busConnection: cms?.routeNotification?.busConnection,
      route: parsePlural(
        fixPlaceholders(cms?.routeNotification?.single),
        fixPlaceholders(cms?.routeNotification?.double)
      ),
      noFlights: cms?.bookingSearch?.errors?.noFlightsFound,
      routeNotSupported: cms?.bookingSearch?.errors?.routeNotSupported,
      timetable: {
        checkTimeTableText: cms?.routeNotification?.timetable?.checkTimeTableText,
        forRouteLinkText: cms?.routeNotification?.timetable?.forRouteLinkText,
        show: cms?.routeNotification?.timetable?.show,
        hide: cms?.routeNotification?.timetable?.hide,
      },
      partnerFlights: cms?.routeNotification?.partnerFlights,
      multiCity: {
        noFlightsFound: cms?.bookingSearch?.errors?.multiCity?.noFlightsFound,
        seeMoreLinkText: cms?.bookingSearch?.errors?.multiCity?.seeMoreLinkText,
        seeMoreLink: {
          url: cms?.bookingSearch?.errors?.multiCity?.seeMoreLink?.url,
        },
      },
      kuusamo: cms?.kuusamoNotification,
    },
    airCalendar: {
      title: cms?.airCalendar?.title,
      departureTitle: cms?.departure,
      returnTitle: cms?.returnDate,
      flexibleTravelDates: cms?.airCalendar?.flexibleTravelDates,
    },
    action: {
      close: cms?.actions?.close,
      remove: cms?.actions?.remove,
      done: cms?.actions?.done,
      loading: cms?.loader?.title,
      reset: cms?.actions?.reset,
      cancel: cms?.actions?.cancel,
      continue: cms?.actions?.continue,
    },
    bookingWidget: {
      title: cms?.bookingSearch?.title,
      departure: cms?.bookingSearch?.departure,
      return: cms?.bookingSearch?.return,
    },
    backgroundImage: cms?.picture,
    aurinkomatkat: {
      title: cms?.aurinkomatkat?.title,
      inCooperation: cms?.aurinkomatkat?.inCooperation,
      paxAmount: {
        childAgeRange: cms?.aurinkomatkat?.childAgeRange,
        childAgeRule: cms?.aurinkomatkat?.childAgeRule,
        room: cms?.aurinkomatkat?.paxAmount?.room,
        addRoom: cms?.aurinkomatkat?.paxAmount?.addRoom,
        removeRoom: fixPlaceholders(cms?.aurinkomatkat?.paxAmount?.removeRoom),
        selectAge: cms?.aurinkomatkat?.paxAmount?.selectAge,
        year: cms?.aurinkomatkat?.paxAmount?.year,
        years: cms?.aurinkomatkat?.paxAmount?.years,
      },
      calendar: {
        recommendedDates: cms?.aurinkomatkat?.calendar?.recommendedDates,
      },
      duration: cms?.aurinkomatkat?.duration,
      selectDuration: cms?.aurinkomatkat?.selectDuration,
      notification: {
        teaser: cms?.aurinkomatkat?.notification?.teaser,
        content: cms?.aurinkomatkat?.notification?.content,
        showMore: cms?.bookingSearch?.readMore,
        showLess: cms?.bookingSearch?.readLess,
      },
      continueModal: {
        title: fixPlaceholders(cms?.aurinkomatkat?.continueModal?.title),
        contentTitle: cms?.aurinkomatkat?.continueModal?.contentTitle,
        content: cms?.aurinkomatkat?.continueModal?.content,
      },
      priceText: cms?.aurinkomatkat?.priceText,
    },
    bookingWidgetTabs: {
      tabs: {
        award: cms?.bookingWidgetTabs?.award,
        flight: cms?.bookingWidgetTabs?.flight,
        aurinkomatkat: cms?.bookingWidgetTabs?.flightAndHotel,
      },
      next: cms?.carousel?.next,
      previous: cms?.carousel?.previous,
    },
    previousSearches: {
      title: cms?.previousSearches?.title,
    },
    award: {
      login: {
        title: cms?.award?.login?.title,
        description: cms?.award?.login?.description,
        login: cms?.award?.login?.login,
        join: cms?.award?.login?.join,
      },
      moveUpToFinnairPlus: {
        title: cms?.award?.moveUpToFinnairPlus?.title,
        description: cms?.award?.moveUpToFinnairPlus?.description,
        signup: cms?.award?.moveUpToFinnairPlus?.signup,
      },
      corporate: {
        title: cms?.award?.corporate?.title,
        description: cms?.award?.corporate?.description,
        login: cms?.award?.corporate?.login,
        signup: cms?.award?.corporate?.signup,
      },
      notifications: {
        totalPoints: fixPlaceholders(cms?.award?.notifications?.totalPoints, '{{points}}'),
      },
      pointsAmount: cms?.award?.pointsAmount,
    },
    promoCode: {
      emptyButton: cms?.promoCode?.emptyButton,
      buttonWithCode: cms?.promoCode?.buttonWithCode,
      modalTitle: cms?.promoCode?.modalTitle,
      loginWarning: cms?.promoCode?.loginWarning,
      inputLabel: cms?.promoCode?.inputLabel,
      inputValidationError: cms?.promoCode?.inputValidationError,
      removeCodeButton: cms?.promoCode?.removeCodeButton,
      promoLinkText: cms?.promoCode?.promoLinkText,
      termsLink: {
        url: cms?.promoCode?.termsLink?.url,
      },
      checkCodeButton: cms?.promoCode?.checkCodeButton,
      termsTitle: cms?.promoCode?.termsTitle,
      notFoundError: cms?.promoCode?.notFoundError,
      genericError: cms?.promoCode?.genericError,
    },
    dynamicNotifications: {
      destination: cms?.dynamicNotifications?.destination,
    },
    multiCityTooltip: {
      description: cms?.multiCity?.description,
      linkText: cms?.multiCity?.linkText,
      link: {
        url: cms?.multiCity?.url,
      },
    },
  };

  return normalizeCurlyBraces(mergeDeep([localizations, parsed], MERGE_OPTIONS));
};
